<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { CheckDataQuery, CheckDataQueryVariables, CheckType } from '@/generated/graphql'
import { computed } from 'vue'
import useCurrentProductionBatch from '@/components/operator/currentProductionBatch'
import ConfirmationCheck from '@/components/check/ConfirmationCheck.vue'
import StringCheck from '@/components/check/StringCheck.vue'
import NumericCheck from '@/components/check/NumericCheck.vue'
import FileUploadCheck from '@/components/check/FileUploadCheck.vue'

const props = defineProps<{
  stepId: string
  disabled?: boolean
}>()

type LocalCheckConfiguration = CheckDataQuery['product']['checkConfigurations'][0]
type LocalCheck = LocalCheckConfiguration['unreleasedChecks'][0]
type LocalConfirmCheck = LocalCheck & { __typename: 'ConfirmCheck' }
type LocalNumericCheck = LocalCheck & { __typename: 'NumericCheck' }
type LocalStringCheck = LocalCheck & { __typename: 'StringCheck' }
type LocalFileUploadCheck = LocalCheck & { __typename: 'FileUploadCheck' }

const { t } = useI18n()
const { currentProductionBatch } = useCurrentProductionBatch()

const fetchQuery = useQuery<CheckDataQuery, CheckDataQueryVariables>(
  gql`
    query CheckData($stepId: ID!) {
      product {
        checkConfigurations(stepId: $stepId) {
          id
          type
          isRequired
          description
          lowerLimit
          upperLimit
          unit
          allowedMimeTypes
          unreleasedChecks {
            ... on CheckI {
              id
              poNumber
              serialNumber
            }
            ... on ConfirmCheck {
              confirmed
            }
            ... on StringCheck {
              text
            }
            ... on NumericCheck {
              milliQuantity
            }
            ... on FileUploadCheck {
              upload {
                id
                name
                contentType
              }
            }
          }
        }
      }
    }
  `,
  () => ({
    stepId: props.stepId,
  }),
)
const configs = computed<LocalCheckConfiguration[]>(
  () => fetchQuery.result.value?.product.checkConfigurations || [],
)

const activeBatch = computed(() => (props.disabled ? undefined : currentProductionBatch.value))
const canPerform = computed(() => !!activeBatch.value)
const serialNumbers = computed(() => activeBatch.value?.serialNumbers ?? [])
// Batches without serial numbers save their checks at PO level, modeled as one empty serial number
const checkRows = computed(() => (serialNumbers.value.length > 0 ? serialNumbers.value : ['']))

const value = (config: LocalCheckConfiguration, serialNumber: string) =>
  config.unreleasedChecks.find(
    (c) => c.poNumber == activeBatch.value?.poNumber && c.serialNumber == serialNumber,
  )
const confirmationValue = (config: LocalCheckConfiguration, serialNumber: string) =>
  (value(config, serialNumber) as LocalConfirmCheck | undefined)?.confirmed
const stringValue = (config: LocalCheckConfiguration, serialNumber: string) =>
  (value(config, serialNumber) as LocalStringCheck | undefined)?.text
const numericValue = (config: LocalCheckConfiguration, serialNumber: string) =>
  (value(config, serialNumber) as LocalNumericCheck | undefined)?.milliQuantity
const uploadValue = (config: LocalCheckConfiguration, serialNumber: string) =>
  (value(config, serialNumber) as LocalFileUploadCheck | undefined)?.upload
</script>

<template>
  <div v-if="configs.length > 0" v-bind="$attrs">
    <h2 :class="{ 'check-unused': !canPerform }">{{ t('entity.check.plural') }}</h2>
    <v-row>
      <template v-for="config in configs" :key="config.id">
        <v-col cols="12" md="6" lg="4" xl="3">
          <v-tooltip location="top" :disabled="canPerform || disabled">
            <template #activator="{ props: tooltipProps }">
              <div v-bind="tooltipProps">
                <v-card :class="{ 'check-unused': !canPerform }" :inert="!canPerform">
                  <v-card-text>
                    <p>
                      {{ config.description }}
                      <span v-if="config.isRequired && serialNumbers.length == 0" class="text-error"
                        >*</span
                      >
                    </p>

                    <template v-for="s in checkRows" :key="s">
                      <confirmation-check
                        v-if="config.type == CheckType.Confirmation"
                        :required="config.isRequired"
                        :value="confirmationValue(config, s)"
                        :check-configuration-id="config.id"
                        :po-number="activeBatch?.poNumber"
                        :serial-number="s"
                        density="compact"
                      />
                      <string-check
                        v-if="config.type == CheckType.String"
                        :required="config.isRequired"
                        :value="stringValue(config, s)"
                        :check-configuration-id="config.id"
                        :po-number="activeBatch?.poNumber"
                        :serial-number="s"
                        density="compact"
                      />
                      <numeric-check
                        v-if="config.type == CheckType.Numeric"
                        :required="config.isRequired"
                        :value="numericValue(config, s)"
                        :check-configuration-id="config.id"
                        :po-number="activeBatch?.poNumber"
                        :serial-number="s"
                        density="compact"
                        :lower-limit="config.lowerLimit"
                        :upper-limit="config.upperLimit"
                        :unit="config.unit"
                      />
                      <file-upload-check
                        v-if="config.type == CheckType.FileUpload"
                        :required="config.isRequired"
                        :value="uploadValue(config, s)"
                        :check-configuration-id="config.id"
                        :po-number="activeBatch?.poNumber"
                        :serial-number="s"
                        :allowed-mime-types="config.allowedMimeTypes"
                      />
                    </template>
                  </v-card-text>
                </v-card>
              </div>
            </template>
            {{ t('component.stepChecks.noProductionBatchHint') }}
          </v-tooltip>
        </v-col>
      </template>
    </v-row>
  </div>
</template>

<style scoped lang="scss">
.check-unused {
  opacity: 0.5;
}
</style>
